/**
 * The teacher area's own look (ADR 0007): shared primitives stay unstyled, so everything with
 * an appearance lives here rather than in `shared/`. Light, plain, table-first — deliberately
 * not the student's black and yellow.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";
import { Button } from "../shared/primitives/Button.js";
import { CloseIcon } from "../shared/primitives/Icons.js";

type ButtonVariant = "primary" | "secondary" | "danger" | "quiet";

const VARIANTS: Record<ButtonVariant, string> = {
  primary: "bg-[var(--color-accent)] text-[var(--color-accent-ink)] hover:brightness-110",
  secondary:
    "bg-[var(--color-surface)] text-[var(--color-ink)] border border-[var(--color-line)] hover:bg-[#f5f5f4]",
  danger:
    "bg-[var(--color-surface)] text-[var(--color-danger)] border border-[#fecaca] hover:bg-[#fef2f2]",
  quiet: "text-[var(--color-accent)] hover:underline",
};

/**
 * The look of a button, separate from the element. A destination is an `<a>`, not a `<button>`
 * wrapping one — nesting them is invalid HTML and gives keyboard and screen-reader users two
 * stops for one action, with the button's own padding doing nothing when clicked.
 */
export function buttonClasses(variant: ButtonVariant = "secondary", className = ""): string {
  const base =
    variant === "quiet"
      ? "text-[15px] font-semibold disabled:opacity-50"
      : "rounded-lg px-3.5 py-2 text-[15px] font-semibold disabled:opacity-50";
  return base + " " + VARIANTS[variant] + " " + className;
}

export function TButton({
  variant = "secondary",
  className = "",
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant }) {
  return <Button className={buttonClasses(variant, className)} {...props} />;
}

export function Panel({
  title,
  actions,
  children,
  className = "",
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={
        "rounded-xl border border-[var(--color-line)] bg-[var(--color-surface)] " + className
      }
    >
      {(title || actions) && (
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--color-line)] px-4 py-3">
          {typeof title === "string" ? (
            <h2 className="m-0 text-[17px] font-bold">{title}</h2>
          ) : (
            title
          )}
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[14px] font-semibold text-[var(--color-ink)]">{label}</span>
      {children}
      {hint && <span className="text-[13px] text-[var(--color-muted)]">{hint}</span>}
    </label>
  );
}

export const inputClass =
  "rounded-lg border border-[var(--color-line)] bg-[var(--color-surface)] px-3 py-2 text-[15px] text-[var(--color-ink)] outline-none focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/20";

export function ErrorLine({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="m-0 text-[14px] font-semibold text-[var(--color-danger)]">
      {children}
    </p>
  );
}

export function Loading({ what = "Wird geladen…" }: { what?: string }) {
  return (
    <p role="status" className="m-0 px-4 py-8 text-center text-[15px] text-[var(--color-muted)]">
      {what}
    </p>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return (
    <p className="m-0 px-4 py-8 text-center text-[15px] text-[var(--color-muted)]">{children}</p>
  );
}

/**
 * ADR 0005 bans `alert` and `confirm` outright, so every dialog is ours. Focus moves in on
 * open and Escape closes, because a teacher on a laptop uses the keyboard.
 */
export function Dialog({
  title,
  onClose,
  children,
  labelledBy = "dialog-title",
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  labelledBy?: string;
}) {
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    panel.current?.focus();
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        className="w-full max-w-lg rounded-xl bg-[var(--color-surface)] p-5 shadow-xl outline-none"
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 id={labelledBy} className="m-0 text-[19px] font-bold">
            {title}
          </h2>
          <Button
            onClick={onClose}
            aria-label="Schliessen"
            className="rounded-full p-1.5 text-[var(--color-muted)] hover:bg-[#f5f5f4]"
          >
            <CloseIcon />
          </Button>
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * The confirmation every destructive action goes through (build spec 5). When `confirmText` is
 * given the teacher has to type it — that is the class deletion, where a misclick would take
 * a whole class's students, chapters and attempts with it (spec 4.5).
 */
export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  confirmText,
  pending,
  error,
  onConfirm,
  onClose,
}: {
  title: string;
  body: ReactNode;
  confirmLabel: string;
  confirmText?: string;
  pending?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const [typed, setTyped] = useState("");
  const blocked = confirmText !== undefined && typed.trim() !== confirmText;

  return (
    <Dialog title={title} onClose={onClose}>
      <div className="flex flex-col gap-4">
        <div className="text-[15px] leading-relaxed text-[var(--color-ink)]">{body}</div>
        {confirmText !== undefined && (
          <Field label={"Tippe «" + confirmText + "», um zu bestätigen"}>
            <input
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              className={inputClass}
              autoComplete="off"
            />
          </Field>
        )}
        {error && <ErrorLine>{error}</ErrorLine>}
        <div className="flex justify-end gap-2">
          <TButton onClick={onClose}>Abbrechen</TButton>
          <TButton variant="danger" onClick={onConfirm} disabled={blocked || pending}>
            {pending ? "…" : confirmLabel}
          </TButton>
        </div>
      </div>
    </Dialog>
  );
}

/** Europe/Zurich, the only timezone the pilot runs in (spec 5). */
export function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("de-CH", {
    timeZone: "Europe/Zurich",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
}

export function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("de-CH", {
    timeZone: "Europe/Zurich",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date(iso));
}
